/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';

import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {ApiConfig} from '../../config/api-config';
import {AdnAssetHelperService} from '../../../components/util/adn-asset-helper.service';
import {AdnHttpProgressCallback} from '../../../components/util/adn-http.service';
import {MEDIA_TYPE, MediaTypeHelper} from '../../../components/util/media-type-helper';
import {Uuid} from '../../../components/util/uuid';

@Injectable({providedIn: 'root'})
export class AssetService {
  constructor(
    private readonly adnAssetHelper: AdnAssetHelperService,
    private readonly client: AdnApiClientService,
    private readonly apiConfig: ApiConfig
  ) {
    this.helpfulBulkUpload = this.helpfulBulkUpload.bind(this);
    this.bulkUpload = this.bulkUpload.bind(this);
    this.bulkThirdPartyUpload = this.bulkThirdPartyUpload.bind(this);
    this.layoutSuggestionsFromCreative = this.layoutSuggestionsFromCreative.bind(this);
    this.fromCreative = this.fromCreative.bind(this);
    this.update = this.update.bind(this);
    this.bulkSave = this.bulkSave.bind(this);
    this.upload = this.upload.bind(this);
  }

  helpfulBulkUpload(parentId: any, data: any, onProgress?: AdnHttpProgressCallback): any {
    return onProgress
      ? this.adnAssetHelper.helpfulUpload(['assets'], parentId, data, onProgress)
      : this.adnAssetHelper.helpfulUpload(['assets'], parentId, data);
  }

  bulkUpload(parentId: any, data: any, onProgress?: AdnHttpProgressCallback): any {
    const assetId = Uuid.generate();
    return onProgress
      ? this.adnAssetHelper.upload(['asset'], null, data, assetId, onProgress)
      : this.adnAssetHelper.upload(['asset'], null, data, assetId);
  }

  bulkThirdPartyUpload(parentId: any, data: any, onProgress?: AdnHttpProgressCallback): any {
    return onProgress
      ? this.adnAssetHelper.upload(['bulkcreatives', 'thirdparty'], null, data, undefined, onProgress)
      : this.adnAssetHelper.upload(['bulkcreatives', 'thirdparty'], null, data);
  }

  layoutSuggestionsFromCreative(id: any, creativeIds: any): any {
    return this.adnAssetHelper.fromParentAsJson('assets', id, _.merge(
      _.isArray(creativeIds) ? {id: creativeIds.join(';'), pageSize: creativeIds.length} : {},
      {responseType: 'LAYOUT_SUGGESTIONS'}
    ));
  }

  fromCreative(id: any, creativeIds: any): any {
    return this.adnAssetHelper.fromParentAsJson(
      'assets',
      id,
      _.isArray(creativeIds) ? {id: creativeIds.join(';'), pageSize: creativeIds.length} : {}
    );
  }

  update(parentId: any, asset: any, assetId?: any): any {
    const uri = this.apiConfig.getVersionedUri(['assets', parentId, assetId || asset.id]);
    return firstValueFrom(this.client.request({method: 'POST', url: uri, body: _.omit(asset, 'id')}));
  }

  bulkSave(parentId: any, assets: any): any {
    const uri = parentId
      ? this.apiConfig.getVersionedUri(['assets', parentId])
      : this.apiConfig.getVersionedUri(['asset']);
    return firstValueFrom(this.client.request({method: 'POST', url: uri, body: assets}));
  }

  upload(creativeId: any, data: any, assetId?: any, onProgress?: AdnHttpProgressCallback): any {
    const isZip = MediaTypeHelper.isMediaType(data.file.type, MEDIA_TYPE.zip);
    const path = data.file && isZip ? 'zippedassets' : 'assets';
    const resolvedAssetId = isZip ? undefined : assetId || Uuid.generate();
    return onProgress
      ? this.adnAssetHelper.upload(path, creativeId, data, resolvedAssetId, onProgress)
      : this.adnAssetHelper.upload(path, creativeId, data, resolvedAssetId);
  }
}
